import { Component, HostListener, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CACHE_EXPIRATION_TIME, DESKTOP_SCREEN_SIZE } from 'app/common/personalConstants';
import {
    ContractorDisplay,
    ContractorState,
    Incident,
    ServiceOffering,
    Vendor
} from 'app/shared/models/claim-tracker.model';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Component({
    selector: 'is-contractors',
    templateUrl: './contractors.component.html',
    styleUrls: ['./contractors.component.scss'],
    standalone: false
})
export class ContractorsComponent implements OnInit {
    @Input() claimNumber!: string;
    @Input() useCaching!: boolean;

    contractors: ContractorDisplay[] = [];
    contractorState: ContractorState = ContractorState.LOADING;
    readonly ContractorStateEnum = ContractorState;
    private hasLoaded = false;
    private policyNumber!: string;
    private contactPublicId!: string;
    private userType!: string;

    private static readonly LOG_PREFIX = 'ContractorsComponent';

    constructor(
        private claimsTrackerService: ClaimsTrackerService,
        private utils: UtilService,
        private logger: LoggerService,
        private sessionService: SessionService,
        private router: Router
    ) {}

    ngOnInit(): void {
        if (!this.hasLoaded && this.claimNumber) {
           this.initializeSessionData();
            this.initializeLogger();


            const CONTRACTOR_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('CONTRACTOR_DATA') : undefined;
            if (
                typeof CONTRACTOR_CACHE_DATA === 'undefined' ||
                CONTRACTOR_CACHE_DATA.id !== this.claimNumber
            ) {
                this.loadContractors();
            } else {
                const allServiceOfferings = CONTRACTOR_CACHE_DATA.value;

                this.contractors = this.transformVendorsToContractors(allServiceOfferings);

                if (this.contractors.length === 0) {
                    this.logger.info('No contractors found after filtering');
                    this.contractorState = ContractorState.NO_OFFERINGS;
                    window.sessionStorage.setItem('contractor', ContractorState.NO_OFFERINGS);
                } else {
                    this.logger.info(`${this.contractors.length} contractors ready to display`);
                    this.contractorState = ContractorState.HAS_CONTRACTORS;
                    window.sessionStorage.setItem('contractor', ContractorState.HAS_CONTRACTORS);
                }
            }
            this.hasLoaded = true;
        }
    }

    private initializeLogger(): void {
        this.logger.setComponentName('ContractorsComponent');
        this.logger.setPageName('Contractors');
        this.logger.info(`${ContractorsComponent.LOG_PREFIX} - Component Initialized`, {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber,
            userType: this.userType
        });
    }

    private initializeSessionData(): void {
        if (!this.claimNumber) {
            this.claimNumber = this.sessionService.getClaimNumber();
        }
        this.contactPublicId = this.sessionService.getSessionItem('claimsContactPublicId');
        this.policyNumber = this.sessionService.getPolicyNumber();
        this.userType = this.sessionService.getUserType();
    }

    loadContractors(): void {
        this.contractorState = ContractorState.LOADING;
        this.logger.info(`Loading contractors for claim: ${this.claimNumber}`);

        this.claimsTrackerService.getAllIncidents(this.claimNumber)
            .pipe(
                catchError((error) => {
                    this.logger.error('Property Contractor: Error loading incidents', error);
                    this.contractorState = ContractorState.ERROR;
                    return of(null);
                })
            )
            .subscribe((incidentsResponse: any) => {
                if (!incidentsResponse || !incidentsResponse.incidents) {
                    this.logger.error('Property Contractor: No incidents data received');
                    this.contractorState = ContractorState.ERROR;
                    return;
                }

                const incidents: Incident[] = incidentsResponse.incidents;
                this.logger.info(`Loaded ${incidents.length} incidents`);

                if (incidents.length === 0) {
                    this.contractorState = ContractorState.NO_OFFERINGS;
                    window.sessionStorage.setItem('contractor', ContractorState.NO_OFFERINGS);
                    return;
                }

                this.loadServiceOfferingsForIncidents(incidents);
            });
    }

    private loadServiceOfferingsForIncidents(incidents: Incident[]): void {
        const serviceOfferingCalls = incidents.map((incident) =>
            this.claimsTrackerService.getIncidentServiceOfferings(this.claimNumber, incident.publicID)
                .pipe(
                    catchError((error) => {
                        this.logger.error(`Property Contractor: Error loading service offerings for incident ${incident.publicID}`, error);
                        return of(null);
                    })
                )
        );

        forkJoin(serviceOfferingCalls).pipe(
            map((responses: any[]) => {
                this.logger.info(`Received ${responses.length} service offering responses`);

                const allServiceOfferings: ServiceOffering[] = [];

                responses.forEach((response, index) => {
                    if (response && response.serviceOfferings) {
                        this.logger.info(`Incident ${incidents[index].publicID} has ${response.serviceOfferings.length} service offerings`);
                        allServiceOfferings.push(...response.serviceOfferings);
                    }
                });

                return allServiceOfferings;
            })
        ).subscribe((allServiceOfferings: ServiceOffering[]) => {
            if (allServiceOfferings.length === 0) {
                this.logger.info('No service offerings found');
                this.contractorState = ContractorState.NO_OFFERINGS;
                window.sessionStorage.setItem('contractor', ContractorState.NO_OFFERINGS);
                return;
            }

            this.utils.setWithExpiry(
                'CONTRACTOR_DATA',
                allServiceOfferings,
                CACHE_EXPIRATION_TIME,
                this.claimNumber
              );

            this.contractors = this.transformVendorsToContractors(allServiceOfferings);

            if (this.contractors.length === 0) {
                this.logger.info('No contractors found after filtering');
                this.contractorState = ContractorState.NO_OFFERINGS;
                window.sessionStorage.setItem('contractor', ContractorState.NO_OFFERINGS);
            } else {
                this.logger.info(`${this.contractors.length} contractors ready to display`);
                this.contractorState = ContractorState.HAS_CONTRACTORS;
                window.sessionStorage.setItem('contractor', ContractorState.HAS_CONTRACTORS);
            }
        });
    }

    private transformVendorsToContractors(serviceOfferings: ServiceOffering[]): ContractorDisplay[] {
        const contractors: ContractorDisplay[] = [];

        serviceOfferings
            .filter((so) => so.type === 'propertyRepair' && so.vendors && so.vendors.length > 0)
            .forEach((so) => {
                so.vendors
                    .filter((v: Vendor) => v.vendorDetailsDTO && !v.vendorDetailsDTO.isCancelled)
                    .forEach((v: Vendor) => {
                        const contractor: ContractorDisplay = {
                            trade: v.vendorDetailsDTO.trade,
                            companyName: v.vendorContactDetailsDTO.companyName,
                            phoneNumber: v.vendorContactDetailsDTO.phoneNumber,
                            address: this.formatAddress(v.vendorContactDetailsDTO)
                        };
                        contractors.push(contractor);
                        this.logger.info(`Added contractor: ${contractor.companyName} - ${contractor.trade}`);
                    });
            });

        return contractors;
    }

    formatAddress(contactDetails: any): string {
        const addressParts: string[] = [];

        if (contactDetails.addressLine1) {
            addressParts.push(contactDetails.addressLine1);
        }
        if (contactDetails.addressLine2) {
            addressParts.push(contactDetails.addressLine2);
        }

        const cityStateParts: string[] = [];
        if (contactDetails.city) {
            cityStateParts.push(contactDetails.city);
        }
        if (contactDetails.state) {
            cityStateParts.push(contactDetails.state);
        }
        if (cityStateParts.length > 0) {
            addressParts.push(cityStateParts.join(', '));
        }

        if (contactDetails.zip) {
            addressParts.push(contactDetails.zip);
        }

        return addressParts.join(', ');
    }

    @HostListener('window:resize')
    onResize(): void {
        if (window.innerWidth >= DESKTOP_SCREEN_SIZE) {
            this.sessionService.setSessionItem('tab', 'Contractors');
            this.router.navigate(['/property/tracker']);
        }
    }
}

